import { createContext, useContext, useState, useEffect } from 'react';
import { 
  signInWithEmailAndPassword,
  signOut,
  onAuthStateChanged,
  createUserWithEmailAndPassword,
  sendPasswordResetEmail
} from 'firebase/auth';
import { 
  doc, 
  setDoc, 
  getDoc,
  serverTimestamp 
} from 'firebase/firestore';
import { auth, db } from '../config/firebase';

const AuthContext = createContext();

export function useAuth() {
  return useContext(AuthContext);
}

export function AuthProvider({ children }) {
  const [currentUser, setCurrentUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [userData, setUserData] = useState(null);

  async function signup(email, password) {
    try {
      // Crear usuario en Authentication
      const userCredential = await createUserWithEmailAndPassword(auth, email, password);
      const user = userCredential.user;

      try {
        // Guardar datos adicionales en Firestore
        await setDoc(doc(db, 'users', user.uid), {
          email: email,
          createdAt: serverTimestamp(),
          lastLogin: serverTimestamp(),
          role: 'admin', // Por defecto para el primer usuario
          status: 'active',
          passwordLastChanged: serverTimestamp(), // Fecha del último cambio de contraseña
          passwordHistory: {
            lastChanged: serverTimestamp(),
            timesChanged: 0
          }
        });
        return userCredential;
      } catch (firestoreError) {
        // Si falla la creación en Firestore, eliminamos el usuario de Authentication
        await user.delete();
        console.error('Error al crear documento en Firestore:', firestoreError);
        throw new Error('Error al crear el perfil de usuario. Por favor, intenta nuevamente.');
      }
    } catch (error) {
      console.error('Error en signup:', error);
      if (error.code === 'auth/email-already-in-use') {
        throw new Error('Este correo electrónico ya está registrado.');
      } else if (error.code === 'auth/weak-password') {
        throw new Error('La contraseña debe tener al menos 6 caracteres.');
      } else if (error.code === 'auth/invalid-email') {
        throw new Error('El correo electrónico no es válido.');
      } else {
        throw new Error('Error al crear la cuenta. Por favor, intenta nuevamente.');
      }
    }
  }

  async function login(email, password) {
    try {
      const userCredential = await signInWithEmailAndPassword(auth, email, password);
      const user = userCredential.user;

      // Actualizar último login en Firestore
      await setDoc(doc(db, 'users', user.uid), {
        lastLogin: serverTimestamp()
      }, { merge: true });

      return userCredential;
    } catch (error) {
      console.error('Error en login:', error);
      throw error;
    }
  }

  async function resetPassword(email) {
    return sendPasswordResetEmail(auth, email);
  }

  function logout() {
    return signOut(auth);
  }

  async function fetchUserData(uid) {
    try {
      const userDoc = await getDoc(doc(db, 'users', uid));
      if (userDoc.exists()) {
        setUserData(userDoc.data());
      }
    } catch (error) {
      console.error('Error al obtener datos del usuario:', error);
    }
  }

  // Agregar función para actualizar metadata de contraseña
  async function updatePasswordMetadata(uid) {
    try {
      const userDoc = await getDoc(doc(db, 'users', uid));
      const userData = userDoc.data();
      
      await setDoc(doc(db, 'users', uid), {
        passwordLastChanged: serverTimestamp(),
        passwordHistory: {
          lastChanged: serverTimestamp(),
          timesChanged: (userData?.passwordHistory?.timesChanged || 0) + 1
        }
      }, { merge: true });
    } catch (error) {
      console.error('Error al actualizar metadata de contraseña:', error);
    }
  }

  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, async (user) => {
      setCurrentUser(user);
      if (user) {
        await fetchUserData(user.uid);
      } else {
        setUserData(null);
      }
      setLoading(false);
    });

    return unsubscribe;
  }, []);

  const value = {
    currentUser,
    userData,
    signup,
    login,
    logout,
    resetPassword
  };

  return (
    <AuthContext.Provider value={value}>
      {!loading && children}
    </AuthContext.Provider>
  );
} 